/**
 * QuickStart information for Weave product.
 */

import {TargetBlank} from '@wandb/weave/common/util/links';
import {Icon} from '@wandb/weave/components/Icon';
import copyToClipboard from 'copy-to-clipboard';
import React, {useCallback} from 'react';
import {Slide, toast} from 'react-toastify';
// eslint-disable-next-line wandb/no-deprecated-imports
import styled from 'styled-components';

import {useViewerApiKeyQuery} from '../../../generated/graphql';
import * as Track from '../../../services/analytics';
import {QuickStartAnalyticsOrigin} from '../../../services/analytics/quickStartEvents';
import {weaveColab} from '../../../util/urls';
import {CodeSnippet} from '../CodeSnippet';
import * as S from '../QuickStart.styles';
type QuickStartWeaveCodeProps = {
  entityName: string;
  projectName: string;
  analyticsOrigin: QuickStartAnalyticsOrigin;
  maxWidth: number;
};

export const Code = styled.code`
  font-family: 'Source Code Pro';
`;
Code.displayName = 'S.Code';

const makeCodeSnippet = (projectName: string) => `import weave
import json
from openai import OpenAI

@weave.op() # 🐝 Decorator to track requests
def extract_fruit(sentence: str) -> dict:
    client = OpenAI()
    system_prompt = "Parse sentences into a JSON dict with keys: fruit, color and flavor."
    response = client.chat.completions.create(
      model="gpt-4o",
      messages=[
        {"role": "system", "content": system_prompt},
        {"role": "user", "content": sentence}
      ],
      temperature=0.7,
      response_format={"type": "json_object"}
    )
    extracted = response.choices[0].message.content
    return json.loads(extracted)

weave.init('${projectName}') # 🐝
sentence = "There are many fruits that were found on the recently discovered planet Goocrux. There are neoskizzles that grow there, which are purple and taste like candy."
extract_fruit(sentence)`;

export const QuickStartWeaveCode = ({
  entityName,
  projectName,
  analyticsOrigin,
  maxWidth,
}: QuickStartWeaveCodeProps) => {
  const viewerApiKey = useViewerApiKeyQuery();
  const apiKey = viewerApiKey.data?.viewer?.apiKey ?? '';

  const onApiKeyCopyClick = useCallback(() => {
    copyToClipboard(apiKey ?? '');
    toast('Copied to clipboard', {
      transition: Slide,
    });

    Track.copiedApiKey({
      projectName,
      entityName,
      location: analyticsOrigin,
      frameworkName: 'weave',
      apiKey,
    });
  }, [analyticsOrigin, apiKey, entityName, projectName]);

  const projectNameStr = projectName === '' ? 'intro-example' : projectName;
  const codeSnippet = makeCodeSnippet(projectNameStr);
  const colabLink = weaveColab();

  return (
    <div style={{paddingTop: '12px'}}>
      <TargetBlank href={colabLink}>
        <S.ColabButton
          variant="contained"
          color="default"
          size="small"
          onClick={() => {
            Track.clickedOpenInColab({
              projectName,
              entityName,
              location: analyticsOrigin,
              frameworkName: 'Weave - None Specified',
              colabLink,
            });
          }}
          startIcon={
            <S.QuickStartIcon>
              <Icon name="logo-colab" />
            </S.QuickStartIcon>
          }>
          Open in Colab
        </S.ColabButton>
      </TargetBlank>
      <S.QuickStartOrderedList>
        <S.QuickStartListItem>Set up the weave library</S.QuickStartListItem>
        <S.QuickStartStepContent>
          <p>Install the CLI and Python library for interacting with weave.</p>
          <CodeSnippet
            codeSnippet="pip install weave"
            analyticsEvent={Track.copiedCodeBlock}
            analyticsDefaultProperties={{
              projectName,
              entityName,
              location: analyticsOrigin,
              frameworkName: 'weave',
            }}
          />
          <p>Next, log in and paste your API key when prompted.</p>
          <CodeSnippet
            codeSnippet="wandb login"
            analyticsEvent={Track.copiedCodeBlock}
            analyticsDefaultProperties={{
              projectName,
              entityName,
              location: analyticsOrigin,
              frameworkName: 'weave',
            }}
          />
          {apiKey && (
            <S.UserApiKeyRow analyticsOrigin={analyticsOrigin}>
              <S.UserApiKeyLabel>
                <S.QuickStartIcon>
                  <Icon name="key-admin" />
                </S.QuickStartIcon>
                <p>
                  <b>Your API key</b> for logging in to the wandb library.
                </p>
              </S.UserApiKeyLabel>
              <S.UserApiKeyBox onClick={onApiKeyCopyClick}>
                <p>{apiKey}</p>
                <S.QuickStartIcon>
                  <Icon name="copy" />
                </S.QuickStartIcon>
              </S.UserApiKeyBox>
            </S.UserApiKeyRow>
          )}
        </S.QuickStartStepContent>
        <S.QuickStartListItem>
          Log a trace to a new project
        </S.QuickStartListItem>
        <S.QuickStartStepContent>
          <p>
            Start tracking inputs and outputs of functions by decorating them
            with <Code>weave.op()</Code>. Run this sample code to see the new
            trace.
          </p>
          <p>
            Note: In this example, we're using <Code>openai</Code> so you should{' '}
            <TargetBlank href="https://platform.openai.com/docs/quickstart/step-2-setup-your-api-key">
              add an openai API key
            </TargetBlank>
            . If you want to use a different LLM, that is not an issue. You can
            add <Code>weave.op()</Code> to any function to begin tracking all of
            its inputs & outputs.
          </p>
          <CodeSnippet
            showExpandCollapse
            codeSnippet={codeSnippet}
            analyticsEvent={Track.copiedCodeBlock}
            analyticsDefaultProperties={{
              projectName,
              entityName,
              location: analyticsOrigin,
              frameworkName: 'weave',
            }}
          />
        </S.QuickStartStepContent>
        <S.QuickStartListItem>
          See traces of your application in your project
        </S.QuickStartListItem>
        <S.QuickStartStepContent>
          <p>
            🎉 Congrats! Now, every time you run your code, weave will
            automatically capture the input & output data and build a tree to
            help you understand how data flows through your application. Run
            this code and your console will output a link to view it within W&B.
          </p>
        </S.QuickStartStepContent>
      </S.QuickStartOrderedList>
    </div>
  );
};
